import { Fragment } from 'react'
import { AREAS, type Area, type Right } from '@wf/shared'
import { InfoTip, type TipLine } from '../ui/InfoTip'

// Rechte-Matrix (Team-Editor). Ersetzt die flache 30-Checkbox-Liste durch eine strukturierte Matrix:
//   • pro Bereich EIN 3-Stufen-Selektor Aus / Nur Lesen / Bearbeiten (Bearbeiten schließt Lesen ein),
//   • die vier heißen Zusatzrechte direkt NEBEN ihrem Bereich (Kontext), + Umsatz/Produktivität/Auftragssicht
//     in einem eigenen Block.
// Rein gesteuert (`value`/`onChange`, ein Right[]). Vergabe-Gate über `grantable` (was der Actor diesem Ziel-Rang
// geben darf) — nicht vergebbare Optionen sind deaktiviert. Backend re-filtert dieselbe Menge (Defense-in-Depth).
//
// Selbsterklärend für Erst-Nutzer: jeder Bereich trägt ein Info-Icon mit Hover-/Fokus-Tooltip, der die drei
// Stufen und das ggf. gepaarte Zusatzrecht in Alltagssprache erklärt (Quelle: docs/BERECHTIGUNGSSYSTEM.md §2.2).
// Heiße Zusatzrechte sind mit ⚠️ + Warn-Tönung markiert. Icons inline als SVG (Lucide-Pfade) — keine
// Dependency, CSP-konform.

const AREA_LABELS: Record<Area, string> = {
  annahme: 'Annahme',
  werkstatt: 'Werkstatt-Board',
  // Namens-Entwirrung (15.07., Plantafel-Feature): 'planung' gatet den DIENSTPLAN (Soll-Zeiten) — die
  // Belegungs-„Plantafel" ist das eigenständige Rechte-Paar 'belegung' (eigene Zeile unter dieser).
  planung: 'Dienstplan',
  kunden: 'Kunden',
  arbeitszeit: 'Arbeitszeit',
  inventar: 'Inventar',
  einkauf: 'Einkauf',
  rechnungen: 'Rechnungen',
  leistungen: 'Leistungen',
  team: 'Team',
  werkstatt_einstellungen: 'Werkstatt-Profil',
}

// Anzeige-Reihenfolge der Bereiche (fachlich gruppiert: Auftrag → Ressourcen → Abrechnung → Verwaltung).
const AREA_ORDER: readonly Area[] = [
  'annahme', 'werkstatt', 'planung', 'kunden', 'arbeitszeit',
  'inventar', 'einkauf', 'rechnungen', 'leistungen', 'team', 'werkstatt_einstellungen',
]

// Tooltip-Texte je Bereich: Zweck + was jede Stufe konkret erlaubt (Alltagssprache, du-Form).
interface AreaHelp { purpose: string; off: string; read: string; edit: string }
const AREA_HELP: Record<Area, AreaHelp> = {
  annahme: {
    purpose: 'Aufträge annehmen und verwalten (Reiter „Annahme“).',
    off: 'Reiter ausgeblendet — keine Auftragsannahme möglich.',
    read: 'Kann Aufträge einsehen, aber nicht anlegen oder ändern.',
    edit: 'Kann Aufträge anlegen und bearbeiten.',
  },
  werkstatt: {
    purpose: 'Tägliche Arbeitssteuerung (Reiter „Werkstatt-Board“): Status-Board einsehen, Aufgaben an Mechaniker zuweisen, Fahrzeug-Status ändern.',
    off: 'Werkstatt-Board komplett ausgeblendet.',
    read: 'Kann das Status-Board und den Stand aller Fahrzeuge einsehen.',
    edit: 'Kann Aufgaben zuweisen und Fahrzeug-Status ändern.',
  },
  planung: {
    purpose: 'Soll-Arbeitszeiten der Mitarbeiter (Dienstplan) — Basis für Über-/Unterstunden im Zeitnachweis.',
    off: 'Dienstplan ausgeblendet.',
    read: 'Kann Dienstpläne einsehen.',
    edit: 'Kann Dienstpläne setzen (nur für rangniedrigere Mitarbeiter).',
  },
  kunden: {
    purpose: 'Die Kundenkartei (Reiter „Kunden“).',
    off: 'Kundenkartei komplett ausgeblendet.',
    read: 'Kann Kundendaten einsehen und suchen, aber nicht neu anlegen oder bearbeiten.',
    edit: 'Kann Kundendaten erstellen und bearbeiten.',
  },
  arbeitszeit: {
    purpose: 'Arbeitszeiten der ANDEREN Mitarbeiter. Das eigene Stempeln ist davon unabhängig und immer möglich.',
    off: 'Sieht nur die eigenen Zeiten.',
    read: 'Kann die Zeiten aller Mitarbeiter einsehen.',
    edit: 'Kann fremde Stempel korrigieren.',
  },
  inventar: {
    purpose: 'Lagerbestand und Teile (Reiter „Inventar“).',
    off: 'Inventar ausgeblendet.',
    read: 'Kann den Bestand einsehen.',
    edit: 'Kann Teile buchen und Bestände ändern.',
  },
  einkauf: {
    purpose: 'Bestellungen und Beschaffung (Reiter „Einkauf“).',
    off: 'Einkauf ausgeblendet.',
    read: 'Kann Bestellungen einsehen.',
    edit: 'Kann bestellen und Bestellungen ändern.',
  },
  rechnungen: {
    purpose: 'Rechnungen und Offene Posten (Reiter „Abrechnung“).',
    off: 'Abrechnung ausgeblendet.',
    read: 'Kann Rechnungen und Offene Posten einsehen.',
    edit: 'Kann Rechnungen erstellen.',
  },
  leistungen: {
    purpose: 'Der Preiskatalog: Arbeitswerte und Stundensätze (Reiter „Leistungen“).',
    off: 'Preiskatalog ausgeblendet.',
    read: 'Kann Preise einsehen.',
    edit: 'Kann Preise anlegen und pflegen.',
  },
  team: {
    purpose: 'Mitarbeiter verwalten — genau diese Seite hier.',
    off: 'Team-Seite ausgeblendet.',
    read: 'Kann die Mitarbeiterliste einsehen.',
    edit: 'Kann Mitarbeiter einladen und Rechte vergeben.',
  },
  werkstatt_einstellungen: {
    purpose: 'Globale Einstellungen deines Betriebs (Reiter „Werkstatt-Profil“): Firmenadresse, Rechnungs-Stammdaten und die gesetzliche AVV-Zustimmung.',
    off: 'Betriebs-Einstellungen ausgeblendet.',
    read: 'Kann die Einstellungen einsehen.',
    edit: 'Kann Einstellungen ändern und die AVV bestätigen.',
  },
}

// Heißes Zusatzrecht, das kontextuell neben einem Bereich steht — inkl. Erklärung fürs Tooltip.
const AREA_HOT: Partial<Record<Area, { right: Right; label: string; hint: string }>> = {
  annahme: {
    right: 'auftrag_loeschen', label: 'Auftrag löschen',
    hint: 'Entfernt Aufträge unwiderruflich — nur an vertrauenswürdige Mitarbeiter vergeben.',
  },
  arbeitszeit: {
    right: 'zeitkorrektur_freigabe', label: 'Zeitkorrektur freigeben',
    hint: 'Genehmigt oder verwirft Korrektur-Anträge der Mitarbeiter an ihren Stempelzeiten.',
  },
  kunden: {
    right: 'kunden_dsgvo', label: 'DSGVO Export/Löschen',
    hint: 'Ermöglicht datenschutzkonformen Daten-Export (Art. 15/20 DSGVO) und Anonymisierung/Löschung nach Art. 17 DSGVO.',
  },
  rechnungen: {
    right: 'rechnung_storno', label: 'Rechnung stornieren',
    hint: 'Hebt gestellte Rechnungen auf (GoBD-relevant) — besonders kritisch, sparsam vergeben.',
  },
}

type Level = 'off' | 'read' | 'edit'
const LEVELS: { key: Level; label: string }[] = [
  { key: 'off', label: 'Aus' },
  { key: 'read', label: 'Nur Lesen' },
  { key: 'edit', label: 'Bearbeiten' },
]

const readRight = (a: Area): Right => `${a}:lesen` as Right
const editRight = (a: Area): Right => `${a}:bearbeiten` as Right

// Plantafel (belegung): eigenständiges Rechte-Paar NEBEN dem Dienstplan-Bereich 'planung' — bewusst KEIN
// AREAS-Eintrag (@wf/shared RIGHTS-Kommentar: diese Matrix ist Record<Area,…>-typisiert). Die Zeile wird
// manuell direkt unter „Dienstplan" gerendert, mit identischer 3-Stufen-Semantik (Bearbeiten ⊇ Lesen).
const BELEGUNG_LABEL = 'Plantafel'
const BELEGUNG_READ: Right = 'belegung:lesen'
const BELEGUNG_EDIT: Right = 'belegung:bearbeiten'
const BELEGUNG_HELP: AreaHelp = {
  purpose: 'Belegungsplanung (Reiter „Plantafel“): Aufträge auf Arbeitsplätze und Zeitfenster legen.',
  off: 'Plantafel ausgeblendet.',
  read: 'Kann die Plantafel einsehen — sieht damit ALLE terminierten Aufträge der Werkstatt.',
  edit: 'Kann Termine und Arbeitsplätze zuweisen (auch ohne Annahme-Recht).',
}
function belegungLevel(caps: ReadonlySet<Right>): Level {
  if (caps.has(BELEGUNG_EDIT)) return 'edit'
  if (caps.has(BELEGUNG_READ)) return 'read'
  return 'off'
}
function withBelegungLevel(caps: readonly Right[], level: Level): Right[] {
  const set = new Set(caps)
  set.delete(BELEGUNG_READ)
  set.delete(BELEGUNG_EDIT)
  if (level === 'read') set.add(BELEGUNG_READ)
  if (level === 'edit') set.add(BELEGUNG_EDIT)
  return [...set]
}

function areaLevel(caps: ReadonlySet<Right>, area: Area): Level {
  if (caps.has(editRight(area))) return 'edit'
  if (caps.has(readRight(area))) return 'read'
  return 'off'
}

function withAreaLevel(caps: readonly Right[], area: Area, level: Level): Right[] {
  const set = new Set(caps)
  set.delete(readRight(area))
  set.delete(editRight(area))
  if (level === 'read') set.add(readRight(area))
  if (level === 'edit') set.add(editRight(area)) // Bearbeiten impliziert Lesen (Guard: canReadArea)
  return [...set]
}

function withRight(caps: readonly Right[], right: Right, on: boolean): Right[] {
  const set = new Set(caps)
  if (on) set.add(right)
  else set.delete(right)
  return [...set]
}

/** Auftragssicht als 3-Stufe: keine / nur eigene (meine_auftraege) / alle (alle_auftraege) — exklusiv. */
type Scope = 'none' | 'own' | 'all'
function scopeOf(caps: ReadonlySet<Right>): Scope {
  if (caps.has('alle_auftraege')) return 'all'
  if (caps.has('meine_auftraege')) return 'own'
  return 'none'
}
function withScope(caps: readonly Right[], scope: Scope): Right[] {
  const set = new Set(caps)
  set.delete('alle_auftraege')
  set.delete('meine_auftraege')
  if (scope === 'own') set.add('meine_auftraege')
  if (scope === 'all') set.add('alle_auftraege')
  return [...set]
}

/** Lucide „triangle-alert“ für heiße Zusatzrechte. */
function WarnIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 20h16a2 2 0 0 0 1.73-2Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  )
}

/** Tooltip-Zeilen eines Bereichs: die drei Stufen + ggf. das gepaarte heiße Zusatzrecht. */
function areaTipLines(area: Area): TipLine[] {
  const help = AREA_HELP[area]
  const hot = AREA_HOT[area]
  const lines: TipLine[] = [
    { label: 'Aus', text: help.off },
    { label: 'Nur Lesen', text: help.read },
    { label: 'Bearbeiten', text: help.edit },
  ]
  if (hot) lines.push({ label: hot.label, text: hot.hint, warn: true })
  return lines
}

export interface PermissionsMatrixProps {
  value: readonly Right[]
  onChange: (next: Right[]) => void
  /** Rechte, die der Actor diesem Ziel-Rang vergeben darf (grantableRights). Nicht enthaltene → deaktiviert. */
  grantable: ReadonlySet<Right>
  /** Optional komplett schreibgeschützt (z. B. während des Speicherns). */
  disabled?: boolean
}

export function PermissionsMatrix({ value, onChange, grantable, disabled }: PermissionsMatrixProps) {
  const caps = new Set(value)

  // Ist eine Stufe für den Bereich vergebbar? Aus ist immer erlaubt; Lesen/Bearbeiten brauchen das jeweilige Recht.
  const levelAllowed = (area: Area, level: Level): boolean =>
    level === 'off' || grantable.has(level === 'read' ? readRight(area) : editRight(area))
  const rowLocked = (area: Area): boolean => !grantable.has(readRight(area)) && !grantable.has(editRight(area))

  const belegungLocked = !grantable.has(BELEGUNG_READ) && !grantable.has(BELEGUNG_EDIT)
  const belegungRow = (
    <div className={`wf-matrix__row${belegungLocked ? ' is-locked' : ''}`}>
      <div className="wf-matrix__area">
        <span className="wf-matrix__areaname">{BELEGUNG_LABEL}</span>
        <InfoTip
          title={BELEGUNG_LABEL}
          purpose={BELEGUNG_HELP.purpose}
          lines={[
            { label: 'Aus', text: BELEGUNG_HELP.off },
            { label: 'Nur Lesen', text: BELEGUNG_HELP.read },
            { label: 'Bearbeiten', text: BELEGUNG_HELP.edit },
          ]}
        />
      </div>
      <div className="wf-seg" role="radiogroup" aria-label={BELEGUNG_LABEL}>
        {LEVELS.map((lv) => {
          const on = belegungLevel(caps) === lv.key
          const optDisabled = disabled || (lv.key !== 'off' && !grantable.has(lv.key === 'read' ? BELEGUNG_READ : BELEGUNG_EDIT))
          return (
            <button
              key={lv.key}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={`${BELEGUNG_LABEL}: ${lv.label}`}
              className={`wf-seg__btn${on ? ' is-active' : ''}`}
              disabled={optDisabled}
              onClick={() => onChange(withBelegungLevel(value, lv.key))}
            >
              {lv.label}
            </button>
          )
        })}
      </div>
      <div className="wf-matrix__hot" />
    </div>
  )

  return (
    <div className="wf-matrix" role="group" aria-label="Berechtigungen">
      <div className="wf-matrix__head">
        <span>Bereich</span>
        <span>Zugriff</span>
        <span>Zusatzrecht</span>
      </div>

      {AREA_ORDER.map((area) => {
        const level = areaLevel(caps, area)
        const hot = AREA_HOT[area]
        const locked = rowLocked(area)
        const row = (
          <div className={`wf-matrix__row${locked ? ' is-locked' : ''}`}>
            <div className="wf-matrix__area">
              <span className="wf-matrix__areaname">{AREA_LABELS[area]}</span>
              <InfoTip title={AREA_LABELS[area]} purpose={AREA_HELP[area].purpose} lines={areaTipLines(area)} />
            </div>

            <div className="wf-seg" role="radiogroup" aria-label={AREA_LABELS[area]}>
              {LEVELS.map((lv) => {
                const on = level === lv.key
                const optDisabled = disabled || !levelAllowed(area, lv.key)
                return (
                  <button
                    key={lv.key}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-label={`${AREA_LABELS[area]}: ${lv.label}`}
                    className={`wf-seg__btn${on ? ' is-active' : ''}`}
                    disabled={optDisabled}
                    onClick={() => onChange(withAreaLevel(value, area, lv.key))}
                  >
                    {lv.label}
                  </button>
                )
              })}
            </div>

            <div className="wf-matrix__hot">
              {hot ? (
                <label className={`wf-hotcheck wf-hotcheck--warn${!grantable.has(hot.right) ? ' is-locked' : ''}`} title={hot.hint}>
                  <input
                    type="checkbox"
                    checked={caps.has(hot.right)}
                    disabled={disabled || !grantable.has(hot.right)}
                    onChange={(e) => onChange(withRight(value, hot.right, e.target.checked))}
                  />
                  <span className="wf-hotcheck__warnicon"><WarnIcon /></span>
                  <span>{hot.label}</span>
                </label>
              ) : null}
            </div>
          </div>
        )
        // Die Plantafel-Zeile (belegung, kein AREAS-Bereich) hängt direkt unter „Dienstplan" — fachlich
        // benachbart, technisch ein manuell gerendertes Rechte-Paar (s. Konstanten oben).
        return (
          <Fragment key={area}>
            {row}
            {area === 'planung' ? belegungRow : null}
          </Fragment>
        )
      })}

      <div className="wf-matrix__extras">
        <div className="wf-matrix__extrarow">
          <span className="wf-matrix__area">
            <span className="wf-matrix__areaname">Auftragssicht</span>
            <InfoTip
              title="Auftragssicht"
              purpose="Welche Aufträge der Mitarbeiter überhaupt sieht (z. B. im Board und in Listen)."
              lines={[
                { label: 'Keine', text: 'Sieht keine Auftragslisten.' },
                { label: 'Nur eigene', text: 'Sieht nur die ihm zugewiesenen Aufträge („Meine Liste“) — typisch für Mechaniker.' },
                { label: 'Alle', text: 'Sieht alle Aufträge der Werkstatt.' },
              ]}
            />
          </span>
          <div className="wf-seg" role="radiogroup" aria-label="Auftragssicht">
            {([
              { key: 'none', label: 'Keine' },
              { key: 'own', label: 'Nur eigene' },
              { key: 'all', label: 'Alle' },
            ] as { key: Scope; label: string }[]).map((s) => {
              const on = scopeOf(caps) === s.key
              // „Alle" braucht alle_auftraege; „nur eigene" meine_auftraege; „keine" immer erlaubt.
              const need: Right | null = s.key === 'all' ? 'alle_auftraege' : s.key === 'own' ? 'meine_auftraege' : null
              const optDisabled = disabled || (need != null && !grantable.has(need))
              return (
                <button
                  key={s.key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={`Auftragssicht: ${s.label}`}
                  className={`wf-seg__btn${on ? ' is-active' : ''}`}
                  disabled={optDisabled}
                  onClick={() => onChange(withScope(value, s.key))}
                >
                  {s.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="wf-matrix__toggles">
          <span className="wf-matrix__toggle">
            <label className={`wf-hotcheck${!grantable.has('produktivitaet:lesen') ? ' is-locked' : ''}`}>
              <input
                type="checkbox"
                checked={caps.has('produktivitaet:lesen')}
                disabled={disabled || !grantable.has('produktivitaet:lesen')}
                onChange={(e) => onChange(withRight(value, 'produktivitaet:lesen', e.target.checked))}
              />
              <span>Produktivität sehen</span>
            </label>
            <InfoTip title="Produktivität sehen" purpose="Zeigt Produktivitäts-Statistiken der Mitarbeiter (gestempelte vs. verrechnete Zeit)." />
          </span>
          <span className="wf-matrix__toggle">
            <label className={`wf-hotcheck${!grantable.has('umsatz') ? ' is-locked' : ''}`}>
              <input
                type="checkbox"
                checked={caps.has('umsatz')}
                disabled={disabled || !grantable.has('umsatz')}
                onChange={(e) => onChange(withRight(value, 'umsatz', e.target.checked))}
              />
              <span>Umsatz sehen</span>
            </label>
            <InfoTip title="Umsatz sehen" purpose="Zeigt Umsatz und Betriebszahlen (z. B. Ø-Auftragswert) — üblicherweise nur Chef-Ebene." />
          </span>
          <span className="wf-matrix__toggle">
            <label className={`wf-hotcheck${!grantable.has('audit:lesen') ? ' is-locked' : ''}`}>
              <input
                type="checkbox"
                checked={caps.has('audit:lesen')}
                disabled={disabled || !grantable.has('audit:lesen')}
                onChange={(e) => onChange(withRight(value, 'audit:lesen', e.target.checked))}
              />
              <span>Protokoll sehen</span>
            </label>
            <InfoTip title="Protokoll sehen" purpose="Zeigt das unveränderliche Aktivitätsprotokoll der Werkstatt (wer hat wann was gelöscht, storniert, exportiert …) — üblicherweise nur Chef-Ebene." />
          </span>
        </div>
      </div>
    </div>
  )
}

// Vollständigkeits-Check zur Bauzeit: die Matrix deckt ALLE 33 Rechte ab (22 Bereichs- + 2 Plantafel/belegung +
// Protokoll/audit + 4 gepaarte + Umsatz + Produktivität + 2 Sicht-Scopes). Schlägt fehl, falls RIGHTS wächst,
// ohne dass die Matrix nachgezogen wird.
const _COVERED: readonly Right[] = [
  ...AREAS.flatMap((a) => [readRight(a), editRight(a)]),
  BELEGUNG_READ, BELEGUNG_EDIT,
  'audit:lesen',
  'auftrag_loeschen', 'zeitkorrektur_freigabe', 'kunden_dsgvo', 'rechnung_storno',
  'umsatz', 'produktivitaet:lesen', 'alle_auftraege', 'meine_auftraege',
]
export const MATRIX_COVERED_RIGHTS: ReadonlySet<Right> = new Set(_COVERED)
